import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { campaignsApi, adminApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import PageHeader from '../components/PageHeader';
import { TableScroll, StatGrid } from '../components/ResponsiveTable';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Plus, MessageSquare, Play, Pause, Trash2, Users, Send, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';

/* Tailwind needs every class spelled out, so tones are full strings (same rule as the Dashboard). */
const TONE = {
  primary: { chip: 'bg-primary/15 text-primary ring-primary/25', text: 'text-primary' },
  success: { chip: 'bg-success/15 text-success ring-success/25', text: 'text-success' },
  danger: { chip: 'bg-danger/15 text-danger ring-danger/25', text: 'text-danger' },
  foreground: { chip: 'bg-primary/15 text-primary ring-primary/25', text: 'text-foreground' },
};

/* A stat tile in the Aurora glass card. Extra props (the data-testid) land on the NUMBER, as before. */
function Stat({ icon: Icon, tone, label, value, ...valueProps }) {
  return (
    <div className="aurora-card flex min-w-0 items-center justify-between gap-3 p-4 sm:p-5">
      <div className="min-w-0">
        <p className="truncate text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p
          className={cn('mt-2 font-heading text-2xl font-semibold leading-none tracking-tight tnum', TONE[tone].text)}
          {...valueProps}
        >
          {value}
        </p>
      </div>
      <span className={cn('inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-1', TONE[tone].chip)}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
    </div>
  );
}

const Block = ({ className }) => <div className={cn('animate-pulse rounded-xl bg-secondary/60', className)} />;

function CampaignsSkeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading campaigns" data-testid="campaigns-skeleton">
      <StatGrid cols={4}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="aurora-card flex items-center justify-between gap-3 p-4 sm:p-5">
            <div className="min-w-0 flex-1">
              <Block className="h-3 w-24" />
              <Block className="mt-3 h-7 w-16" />
            </div>
            <Block className="h-10 w-10 rounded-full" />
          </div>
        ))}
      </StatGrid>
      <Block className="h-9 w-full sm:w-[180px]" />
      <div className="aurora-card space-y-3 p-5 sm:p-6">
        <Block className="h-4 w-28" />
        {[0, 1, 2, 3].map((i) => <Block key={i} className="h-10 w-full" />)}
      </div>
    </div>
  );
}

export default function CampaignsPage() {
  usePageTitle('Campaigns');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const queryClient = useQueryClient();

  const { data: campaigns, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['campaigns', filterStatus],
    queryFn: () => campaignsApi.getCampaigns(filterStatus !== 'all' ? { status: filterStatus } : {}).then(res => res.data),
  });

  const { data: slots } = useQuery({
    queryKey: ['slots'],
    queryFn: () => adminApi.getSlots().then(res => res.data),
  });

  const createMutation = useMutation({
    mutationFn: campaignsApi.createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      setCreateDialogOpen(false);
      toast.success('Campaign created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ campaignId, data }) => campaignsApi.updateCampaign(campaignId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      toast.success('Campaign updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update campaign');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: campaignsApi.deleteCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries(['campaigns']);
      setSelectedCampaign(null);
      toast.success('Campaign deleted');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to delete campaign');
    },
  });

  const handleCreateCampaign = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const usernames = formData.get('target_usernames')
      .split('\n')
      .map(u => u.trim().replace('@', ''))
      .filter(u => u.length > 0);

    if (usernames.length === 0) {
      toast.error('Please enter at least one target username');
      return;
    }

    createMutation.mutate({
      name: formData.get('name'),
      slot_name: formData.get('slot_name'),
      target_usernames: usernames,
      message_spintax: formData.get('message_spintax'),
      daily_limit: parseInt(formData.get('daily_limit') || '50'),
      delay_between_dms_min: parseInt(formData.get('delay_min') || '60'),
      delay_between_dms_max: parseInt(formData.get('delay_max') || '180'),
      start_immediately: formData.get('start_immediately') === 'on',
    });
  };

  const handleToggleStatus = (campaign) => {
    const newStatus = campaign.status === 'RUNNING' ? 'PAUSED' : 'RUNNING';
    updateMutation.mutate({
      campaignId: campaign.id,
      data: { status: newStatus },
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      RUNNING: { color: 'bg-success/15 text-success border-success/30', icon: Play },
      PAUSED: { color: 'bg-warning/15 text-warning border-warning/30', icon: Pause },
      COMPLETED: { color: 'bg-primary/15 text-primary border-primary/30', icon: CheckCircle },
      PENDING: { color: 'bg-muted/60 text-muted-foreground border-border', icon: AlertCircle },
      CANCELLED: { color: 'bg-danger/15 text-danger border-danger/30', icon: AlertCircle },
    };
    const v = variants[status] || variants.PENDING;
    const Icon = v.icon;
    return (
      <Badge className={`${v.color} border rounded-full whitespace-nowrap`} data-testid="campaign-status-badge">
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  const getProgress = (campaign) => {
    if (!campaign.total_targets || campaign.total_targets === 0) return 0;
    return Math.round(((campaign.sent_count + campaign.failed_count) / campaign.total_targets) * 100);
  };

  const header = (
    <PageHeader
      icon={MessageSquare}
      title="DM Campaigns"
      description="Manage automated direct message campaigns"
      actions={
        <Button
          onClick={() => setCreateDialogOpen(true)}
          className="tap shadow-glow"
          data-testid="create-campaign-button"
        >
          <Plus className="h-4 w-4" />
          New Campaign
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        {header}
        <CampaignsSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      {header}

      {/* Stats Cards */}
      <StatGrid cols={4}>
        <Stat
          icon={Play}
          tone="primary"
          label="Active Campaigns"
          value={campaigns?.filter(c => c.status === 'RUNNING').length || 0}
          data-testid="active-campaigns-count"
        />
        <Stat
          icon={Users}
          tone="foreground"
          label="Total Targets"
          value={campaigns?.reduce((sum, c) => sum + (c.total_targets || 0), 0) || 0}
          data-testid="total-targets-count"
        />
        <Stat
          icon={Send}
          tone="success"
          label="Messages Sent"
          value={campaigns?.reduce((sum, c) => sum + (c.sent_count || 0), 0) || 0}
          data-testid="messages-sent-count"
        />
        <Stat
          icon={AlertCircle}
          tone="danger"
          label="Failed"
          value={campaigns?.reduce((sum, c) => sum + (c.failed_count || 0), 0) || 0}
          data-testid="messages-failed-count"
        />
      </StatGrid>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-[180px] bg-secondary/60 border-border text-foreground" data-testid="filter-status-select">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            <SelectItem value="RUNNING">Running</SelectItem>
            <SelectItem value="PAUSED">Paused</SelectItem>
            <SelectItem value="COMPLETED">Completed</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Campaigns Table */}
      {isError ? (
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="campaigns-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <h3 className="mt-4">Could not load campaigns</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
            {error?.response?.data?.detail || error?.message || 'The server did not answer.'}
          </p>
          <Button variant="outline" onClick={() => refetch()} className="tap mt-5 rounded-full">
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      ) : campaigns && campaigns.length > 0 ? (
        <Card className="aurora-card">
          <CardHeader>
            <CardTitle className="font-heading text-base font-semibold">Campaigns</CardTitle>
          </CardHeader>
          <CardContent>
            <TableScroll>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Slot</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Sent / Failed</TableHead>
                    <TableHead>Daily Limit</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.map((campaign) => (
                    <TableRow key={campaign.id} className="border-border" data-testid="campaign-row">
                      <TableCell className="max-w-[16rem] min-w-0 truncate font-medium text-foreground" data-testid="campaign-name">{campaign.name}</TableCell>
                      <TableCell className="max-w-[12rem] min-w-0 truncate text-muted-foreground">{campaign.slot_name}</TableCell>
                      <TableCell>{getStatusBadge(campaign.status)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 w-32">
                          <Progress value={getProgress(campaign)} className="h-2" />
                          <span className="text-xs text-muted-foreground tnum">{getProgress(campaign)}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="tnum whitespace-nowrap">
                        <span className="text-success">{campaign.sent_count || 0}</span>
                        {' / '}
                        <span className="text-danger">{campaign.failed_count || 0}</span>
                        <span className="text-muted-foreground"> of {campaign.total_targets || 0}</span>
                      </TableCell>
                      <TableCell className="tnum whitespace-nowrap">{campaign.daily_limit}/day</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {campaign.status !== 'COMPLETED' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleToggleStatus(campaign)}
                              className="tap h-8 w-8 text-muted-foreground hover:text-foreground"
                              aria-label={campaign.status === 'RUNNING' ? 'Pause campaign' : 'Start campaign'}
                              title={campaign.status === 'RUNNING' ? 'Pause campaign' : 'Start campaign'}
                              data-testid="campaign-toggle-button"
                            >
                              {campaign.status === 'RUNNING' ? (
                                <Pause className="h-4 w-4" />
                              ) : (
                                <Play className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteMutation.mutate(campaign.id)}
                            className="tap h-8 w-8 text-danger hover:text-danger hover:bg-danger/10"
                            aria-label="Delete campaign"
                            title="Delete campaign"
                            data-testid="campaign-delete-button"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableScroll>
          </CardContent>
        </Card>
      ) : (
        <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid="campaigns-empty">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
            <MessageSquare className="h-6 w-6" />
          </span>
          <h3 className="mt-4">No campaigns yet</h3>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">Create your first DM campaign to start reaching out</p>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="tap mt-5 rounded-full"
          >
            <Plus className="h-4 w-4" />
            Create Campaign
          </Button>
        </div>
      )}

      {/* Create Campaign Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Create DM Campaign</DialogTitle>
            <DialogDescription>
              Set up an automated direct message campaign to reach your target audience.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateCampaign} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="My DM Campaign"
                  className="bg-secondary/60 border-border placeholder:text-muted-foreground/70"
                  required
                  data-testid="campaign-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slot_name">Slot</Label>
                <Select name="slot_name" required>
                  <SelectTrigger className="bg-secondary/60 border-border" data-testid="campaign-slot-select">
                    <SelectValue placeholder="Select slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {slots?.map((slot) => (
                      <SelectItem key={slot.slot_id || slot.slot_name || slot.id} value={slot.slot_id || slot.slot_name}>
                        {slot.slot_label || slot.slot_name} ({slot.slot_id || `slot${slot.slot_number}`})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="target_usernames">Target Usernames (one per line)</Label>
              <Textarea
                id="target_usernames"
                name="target_usernames"
                placeholder="@username1&#10;@username2&#10;@username3"
                className="bg-secondary/60 border-border placeholder:text-muted-foreground/70 min-h-[120px] font-mono"
                required
                data-testid="campaign-targets-input"
              />
              <p className="text-xs text-muted-foreground">Enter Instagram usernames, one per line. The @ symbol is optional.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message_spintax">Message Template (Spintax supported)</Label>
              <Textarea
                id="message_spintax"
                name="message_spintax"
                placeholder="{Hi|Hello|Hey} there! I {noticed|saw} your {profile|account} and thought..."
                className="bg-secondary/60 border-border placeholder:text-muted-foreground/70 min-h-[100px]"
                required
                data-testid="campaign-message-input"
              />
              <p className="text-xs text-muted-foreground">
                Use spintax like {'{option1|option2|option3}'} to randomize your messages.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="daily_limit">Daily Limit</Label>
                <Input
                  id="daily_limit"
                  name="daily_limit"
                  type="number"
                  defaultValue="50"
                  min="1"
                  max="200"
                  className="bg-secondary/60 border-border tnum"
                  data-testid="campaign-daily-limit-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delay_min">Min Delay (sec)</Label>
                <Input
                  id="delay_min"
                  name="delay_min"
                  type="number"
                  defaultValue="60"
                  min="30"
                  className="bg-secondary/60 border-border tnum"
                  data-testid="campaign-delay-min-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="delay_max">Max Delay (sec)</Label>
                <Input
                  id="delay_max"
                  name="delay_max"
                  type="number"
                  defaultValue="180"
                  min="60"
                  className="bg-secondary/60 border-border tnum"
                  data-testid="campaign-delay-max-input"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="start_immediately"
                name="start_immediately"
                defaultChecked
                className="h-4 w-4 rounded border-border accent-primary"
                data-testid="campaign-start-immediately-checkbox"
              />
              <Label htmlFor="start_immediately" className="text-sm font-normal text-foreground/90">
                Start campaign immediately
              </Label>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="tap">
                Cancel
              </Button>
              <Button
                type="submit"
                className="tap"
                disabled={createMutation.isPending}
                data-testid="campaign-submit-button"
              >
                {createMutation.isPending ? 'Creating...' : 'Create Campaign'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
